import React from 'react'
import styled from 'styled-components'
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaTelegramPlane, FaTiktok, FaUsers, FaWhatsapp, FaYoutube } from 'react-icons/fa'
import { IconButton, TextButton } from '@eduact/ed-components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import routes from '@Constants/Routes'
import { Link } from 'react-router-dom'
import { useAppSelector } from '@Hooks/ReduxHooks'
type Props = {}

/* tenant/mribrahimfarid: the students' communities. */
export const FACEBOOK_GROUP_URL = 'https://www.facebook.com/share/g/1NhrxLoWte/'
export const WHATSAPP_CHANNEL_URL = 'https://whatsapp.com/channel/0029VbB8E8A0bIdo43YkRf3M'

const Footer: React.FC<Props> = () => {
	const [t] = useTranslation('layouts', { keyPrefix: 'footer' })
	const history = useNavigate()
	const { about_us_page, fb_link, youtube_link, instagram_link, linkedin_link } = useAppSelector(s => s.settings)
	return (
		<FooterStyled>
			<FooterWrapper>
				<FooterActionsContainer>
					<FooterLinksContainer>
						{about_us_page && (
							<TextButton textDecoration={'underline'} onClick={() => history('/about')} variant="dark" padding={'0'} fontWeight={'bold'}>
								{t('aboutUs')}
							</TextButton>
						)}
						{/* tenant/mribrahimfarid: terms & privacy links removed from the footer.
						    Settings keys terms_link / privacy_link are intentionally unused here. */}
						{/* <a>{t('contact_us')}</a> */}
						{/* <a onClick={() => history(routes.faqs)}>{t('FAQs')}</a> */}
					</FooterLinksContainer>
					{/* tenant/mribrahimfarid: Mr Ibrahim Farid's channels replace the EduAct
					    fallbacks; admin settings keys still override where they exist.
					    TikTok / WhatsApp / Telegram have no settings key — hardcoded. */}
					<FooterSocialLinksContainer>
						{/* the two community pills sit on their own row above the icons on
						    phones, and trail the icon row on desktop */}
						<CommunityRow>
							<GroupPill href={FACEBOOK_GROUP_URL} target="_blank">
								<FaUsers />
								جروب الفيسبوك
							</GroupPill>
							<GroupPill href={WHATSAPP_CHANNEL_URL} target="_blank">
								<FaWhatsapp />
								قناة الواتساب
							</GroupPill>
						</CommunityRow>
						<IconsRow>
						<SocialMediaIcon href={fb_link ?? 'https://www.facebook.com/share/1JxNgE8zAA/?mibextid=wwXIfr'} target="_blank">
							<FaFacebookF />
						</SocialMediaIcon>
						<SocialMediaIcon href={youtube_link ?? 'https://youtube.com/@ibrahimfarid?si=9sA_PXoYx5TH8M82'} target="_blank">
							<FaYoutube />
						</SocialMediaIcon>
						{linkedin_link && (
							<SocialMediaIcon href={linkedin_link} target="_blank">
								<FaLinkedinIn />
							</SocialMediaIcon>
						)}
						<SocialMediaIcon href={instagram_link ?? 'https://www.instagram.com/ibrahim_farid78?igsi=dTZ2NjFucTh0dXc2'} target="_blank">
							<FaInstagram />
						</SocialMediaIcon>
						<SocialMediaIcon href={'https://www.tiktok.com/@ibrahimfarid78?_r=1&_t=ZS-99GPj0jMtca'} target="_blank">
							<FaTiktok />
						</SocialMediaIcon>
						<SocialMediaIcon href={'https://wa.me/201007347077'} target="_blank">
							<FaWhatsapp />
						</SocialMediaIcon>
						<SocialMediaIcon href={'https://t.me/IFMFirst'} target="_blank">
							<FaTelegramPlane />
						</SocialMediaIcon>
						</IconsRow>
					</FooterSocialLinksContainer>
				</FooterActionsContainer>
				<FooterCopyRights>{t('copy_rights')}</FooterCopyRights>
			</FooterWrapper>
		</FooterStyled>
	)
}

export default Footer

const FooterStyled = styled.div`
	background: ${props => props.theme.colors.darkCultured};
	direction: ltr;
	z-index: 2;
	svg {
		transform: unset !important;
	}
`

const FooterWrapper = styled.div`
	padding: 2.5rem 2rem;
	@media (min-width: 1024px) {
		display: flex;
		flex-direction: row-reverse;
		justify-content: space-between;
		padding: 2.25rem 3rem;
		align-items: center;
	}
`
const FooterLinksContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	font-size: 1rem;
	white-space: nowrap;
	place-items: center;
	grid-row-gap: 1rem;
	font-weight: bold;
	@media (min-width: 1024px) {
		display: flex;
		a {
			margin: 0 0.5rem;
			text-decoration: none;
			color: ${props => props.theme.colors.dark};
		}
	}
`
const SocialMediaIcon = styled.a`
	cursor: pointer;
	padding: 0 0.875rem;
	font-size: 1.5rem;
	color: ${props => props.theme.colors.dark};
	&:nth-child(even) {
		border-left: 1px solid ${props => props.theme.colors.platinum} !important;
		border-right: 1px solid ${props => props.theme.colors.platinum} !important;
	}
	&:last-of-type {
		border-right: 0 !important;
	}
`
const GroupPill = styled.a`
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	gap: 0.375rem;
	padding: 0.375rem 0.75rem;
	border: 1.5px solid ${props => props.theme.colors.dark};
	border-radius: 999px;
	font-size: 0.8125rem;
	font-weight: bold;
	color: ${props => props.theme.colors.dark};
	text-decoration: none;
	white-space: nowrap;
	svg {
		font-size: 1rem;
	}
`

/* width:100% on phones — as a flex item in the column it otherwise
   shrink-wraps narrower than the two pills and forces them onto two rows. */
const CommunityRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	flex-wrap: wrap;
	width: 100%;
	@media (min-width: 1024px) {
		width: auto;
	}
`

const IconsRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`

/* phones: community pills on their own row above the icons.
   desktop: one row, icons first and the pills trailing (row-reverse, since
   the footer forces direction: ltr). */
const FooterSocialLinksContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.875rem;
	/* no side padding on phones — the wrapper already pads, and the extra
	   inset squeezed the two pills onto separate rows */
	padding: 0;
	justify-content: center;
	margin: 1.5rem 0;
	@media (min-width: 1024px) {
		flex-direction: row-reverse;
		gap: 1rem;
		padding: 0 2rem;
		margin: 0;
	}
`
const FooterCopyRights = styled.section`
	font-size: 0.9375rem;
	text-align: center;
	font-weight: bold;
`
const FooterActionsContainer = styled.section`
	@media (min-width: 1024px) {
		display: flex;
		align-items: center;
	}
`
